import { useQuery } from '@tanstack/react-query';

import { billingApi } from '@/api/services/billing.api';

import { BILLING_QUERY_KEYS } from '../query-keys';

const ACTIVATION_POLL_MS = 3_000;

interface IUseBillingSubscriptionOptions {
    /**
     * Poll the local snapshot until it reports `subscribed` — covers the
     * post-checkout race where Stripe redirects back before the SA webhook
     * + snapshot push have landed. Self-stops once subscribed.
     */
    readonly pollWhileUnsubscribed?: boolean;
    /** Skip the request entirely (e.g. member lacks billing:read). */
    readonly enabled?: boolean;
}

/** Local subscription snapshot — cheap read, still refreshed often enough
 *  to pick up plan changes made in another tab / by Stripe webhooks. */
export function useBillingSubscription(options?: IUseBillingSubscriptionOptions) {
    return useQuery({
        queryKey: BILLING_QUERY_KEYS.subscription(),
        queryFn: billingApi.getSubscription,
        enabled: options?.enabled ?? true,
        staleTime: 30_000,
        // Opted in against the app-wide `refetchOnWindowFocus: false`, matching
        // `useBillingUsage` / `useBillingLimits`: a purchase finalised while the
        // tab was in the background (Stripe's hosted checkout, another tab, a
        // late webhook) must flip the page on refocus rather than needing F5.
        refetchOnWindowFocus: true,
        refetchInterval: options?.pollWhileUnsubscribed
            ? (query) => (query.state.data?.subscribed ? false : ACTIVATION_POLL_MS)
            : false,
    });
}
